import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * A route's error boundary — and the recovery for a stale build.
 *
 * Every page below sign-in is `React.lazy`, so opening Reports fetches
 * `/assets/Reports-<hash>.js` at the moment it is clicked. This app deploys
 * several times a day and the hash changes with every build, so a tab open
 * since the morning asks for a file the server stopped serving hours ago; the
 * import rejects, React re-throws it during render, and the page is gone.
 * `React.lazy` memoizes the rejection, so "Try again" cannot work — only a
 * fresh document can, and a fresh document fixes it completely, because
 * navigations are network-first. So we reload for the reader, once.
 *
 * ONCE. The guard used to be component state, which does not survive the very
 * reload it performs — so a chunk that STILL would not load after reloading (a
 * half-finished deploy, a CDN serving a truncated file, an origin that is
 * down) reloaded again, and again: a loop, which is a worse failure than the
 * one it papered over. The mark now lives in sessionStorage for 30 seconds —
 * long enough to span the reload, short enough that the next deploy hours
 * later recovers the same way — and a second failure inside the window shows
 * the card and stops. Storage that cannot be written counts as "already
 * reloaded": a browser we cannot leave a note in is one we must not gamble a
 * loop on.
 *
 * `resetKey` (the pathname, in App.tsx) clears a caught error on navigation,
 * so one bad screen never holds the whole app hostage. It used to be
 * `key={location}`, which did the same by REMOUNTING everything below on every
 * path change — the Availability views lost their filters, refetched and
 * replayed the page's entrance each time the reader switched Matrix → Table.
 */

/**
 * A dynamic import() that did not arrive. Every engine words it differently
 * and none sets a useful `name`, so this matches on text: Chrome/Edge "Failed
 * to fetch dynamically imported module", Firefox "error loading dynamically
 * imported module", WebKit "Importing a module script failed" — the one that
 * matters most, since an installed iOS app is where a session lives longest.
 * `Unable to preload CSS` is Vite's wording for the same 404 on a stylesheet.
 */
const CHUNK_ERROR =
  /chunkloaderror|loading chunk \d+ failed|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css|dynamically imported module/i;

export function isChunkError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { name?: unknown; message?: unknown };
  return CHUNK_ERROR.test(`${String(e.name ?? '')} ${String(e.message ?? '')}`);
}

const RELOAD_KEY = 'salefish:chunk-reload-at';
export const RELOAD_WINDOW_MS = 30_000;

/** Did we reload for a missing chunk in the last 30 seconds? */
export function reloadedRecently(now: number = Date.now()): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY));
    return Number.isFinite(at) && at > 0 && now - at < RELOAD_WINDOW_MS;
  } catch {
    return true;
  }
}

export function markReloaded(now: number = Date.now()): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    /* see reloadedRecently */
  }
}

/** The prefix of the cache the worker keeps the app's document in (service-worker.js SHELL_CACHE). */
export const SHELL_CACHE_PREFIX = 'salefish-shell-';

/**
 * Reload into the build the server has NOW, never back into the stale one.
 *
 * A plain reload could land on the same old build: the worker answers a
 * navigation that takes more than three seconds with the document it stored,
 * and on a phone with two bars that document is the one naming the missing
 * chunks (Andrew's iPhone, 2026-10-21: the reload hit the same card). So:
 *
 *   · a newer worker parked and waiting takes over first. Its controllerchange
 *     reloads the page (lib/serviceWorkerRegistration.ts), into the build it
 *     stored whole at install;
 *   · otherwise the stored document is dropped, so the reload waits for the
 *     network instead of racing it.
 *
 * With neither a worker nor Cache Storage (a test, an old browser) it is just a
 * reload.
 */
export function reloadFresh(): void {
  const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
  const store = (globalThis as { caches?: CacheStorage }).caches;
  if (!sw && !store) {
    window.location.reload();
    return;
  }
  void (async () => {
    try {
      const registration = await sw?.getRegistration?.();
      if (registration?.waiting && sw?.controller) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        // controllerchange reloads; this is only for a worker that never takes over.
        setTimeout(() => window.location.reload(), 4_000);
        return;
      }
      const names = (await store?.keys()) ?? [];
      await Promise.all(names.filter((name) => name.startsWith(SHELL_CACHE_PREFIX)).map((name) => store!.delete(name)));
    } catch {
      /* a plain reload is still better than staying here */
    }
    window.location.reload();
  })();
}

/**
 * Reload for a missing chunk if we have not just done so. Returns whether a
 * reload was started.
 */
export function reloadForStaleBuild(): boolean {
  if (reloadedRecently()) return false;
  markReloaded();
  reloadFresh();
  return true;
}

/**
 * The part of an error someone can quote to support: the message, one line,
 * short. Not a stack trace in a buyer's face.
 */
export function shortErrorText(error: unknown, max = 160): string | null {
  const e = error as { name?: unknown; message?: unknown } | null;
  const message = typeof e?.message === 'string' ? e.message : typeof error === 'string' ? error : '';
  const line = message.split('\n')[0].trim();
  if (!line) return null;
  const name = typeof e?.name === 'string' && e.name && e.name !== 'Error' && !line.startsWith(e.name) ? `${e.name}: ` : '';
  const text = `${name}${line}`;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** The translator ErrorCard takes, or the English passthrough when it has none. */
type ErrorCardTranslate = (key: string, en: string) => string;
const ENGLISH_ONLY: ErrorCardTranslate = (_key, en) => en;

/**
 * The card a boundary shows. Plain markup and the Button primitive only — no
 * translation, theme or query hooks — because the outer boundary renders it
 * when one of those providers is the thing that failed. A boundary that sits
 * INSIDE the providers passes `t` (see TranslatedErrorCard); without it the
 * card reads in English.
 */
export function ErrorCard({
  error,
  scope = 'page',
  onRetry,
  className,
  t = ENGLISH_ONLY,
}: {
  error: unknown;
  /** `app` when the whole shell failed (AppErrorBoundary), `page` for a route. */
  scope?: 'page' | 'app';
  onRetry?: () => void;
  className?: string;
  t?: ErrorCardTranslate;
}) {
  const stale = isChunkError(error);
  const detail = shortErrorText(error);
  const Icon = stale ? RefreshCw : AlertTriangle;

  const title = stale
    ? t('shellChromeErrorStaleTitle', 'This page needs a refresh')
    : scope === 'app'
      ? t('shellChromeErrorAppTitle', 'SaleFish Hit a Problem')
      : t('shellChromeErrorPageTitle', 'Something went wrong on this page');
  const body = stale
    ? t('shellChromeErrorStaleBody', 'SaleFish was updated while this was open, and part of this page is no longer available. Reloading fetches the current version.')
    : t('shellChromeErrorBody', 'Nothing you did caused this, and nothing you saved has been lost. Reloading usually clears it.');

  return (
    <div
      role="alert"
      className={className ?? 'flex min-h-[50dvh] flex-col items-center justify-center p-8 text-center'}
      /* Marker for scripts/fixture-audit.mjs: a route that THREW renders this
         card, and a crash is the loudest thing a route sweep can find. It must
         not be the one thing the sweep cannot see. */
      data-error-boundary=""
    >
      <div className="mx-auto flex max-w-md flex-col items-center gap-stack">
        <span
          aria-hidden="true"
          className="flex size-12 items-center justify-center rounded-full border border-border bg-foreground/[.04] text-muted-foreground"
        >
          <Icon className="size-[22px]" />
        </span>
        <h2 className="text-title font-bold">{title}</h2>
        <p className="text-body text-muted-foreground">{body}</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button onClick={() => (stale ? reloadFresh() : window.location.reload())} data-testid="button-error-reload">
            <RefreshCw />
            {t('shellChromeErrorReload', 'Reload')}
          </Button>
          {!stale && onRetry && (
            <Button variant="outline" onClick={onRetry} data-testid="button-error-retry">
              {t('shellChromeErrorTryAgain', 'Try Again')}
            </Button>
          )}
          {!stale && scope === 'page' && (
            <Button variant="outline" onClick={() => { window.location.href = '/projects'; }} data-testid="button-error-home">
              {t('shellChromeErrorGoHome', 'Go Home')}
            </Button>
          )}
        </div>
        {/* Terse, muted, selectable: enough to quote into a support message. */}
        {detail && (
          <p className="max-w-full break-words text-caption text-muted-foreground select-text" data-testid="text-error-detail">
            {detail}
          </p>
        )}
      </div>
    </div>
  );
}

interface Props {
  children: ReactNode;
  /**
   * What to show instead of the recovery card. `null` for a slot that holds a
   * modal or a floating control: if its chunk cannot load, the thing simply
   * does not appear. The stale-build reload still runs either way.
   */
  fallback?: ReactNode;
  /**
   * When this changes, a caught error is cleared and the children are tried
   * again — without remounting them when there was no error.
   */
  resetKey?: unknown;
}

/**
 * ErrorCard in the reader's language. Every ChunkErrorBoundary is mounted
 * inside the query and language providers (App.tsx), so the hook is safe here;
 * if one ever were not, this throws to AppErrorBoundary, whose card is English.
 */
function TranslatedErrorCard(props: Omit<Parameters<typeof ErrorCard>[0], 't'>) {
  const t = useTranslation();
  return <ErrorCard {...props} t={t} />;
}

interface State {
  error: unknown;
  /** True between deciding to reload and the document actually going away. */
  reloading: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error ?? new Error('Unknown render error') };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && !this.state.reloading && !Object.is(prev.resetKey, this.props.resetKey)) {
      this.setState({ error: null, reloading: false });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Always logged, with the component stack: in a minified build it is the
    // only thing that names the component that threw.
    if (!isChunkError(error)) {
      console.error('[ChunkErrorBoundary] Unhandled render error:', error, '\nComponent stack:', info.componentStack);
      return;
    }
    if (reloadForStaleBuild()) this.setState({ reloading: true });
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    // The document is on its way out; nothing worth reading has time to show.
    if (reloading) return this.props.fallback !== undefined ? this.props.fallback : <div className="min-h-[50dvh]" />;
    if (this.props.fallback !== undefined) return this.props.fallback;
    return <TranslatedErrorCard error={error} onRetry={() => this.setState({ error: null, reloading: false })} />;
  }
}
